import styled from 'styled-components';

import { EMPTY_VALUES } from '@/_pages/interwill/components/Dashboard/AssetDitribution';
import { BarStackedChart } from '@/_pages/interwill/components/Dashboard/charts/BarStackedChart';
import { DoughnutChart } from '@/_pages/interwill/components/Dashboard/charts/DoughnutChart';
import { DashboardItem } from '@/_pages/interwill/components/Dashboard/DashboardItem';
import {
  DoughnutChartWrapper,
  Legend,
  StyledSegmented as Segmented,
  TextContainer,
} from '@/_pages/interwill/components/Dashboard/styled';
import {
  OWNERSHIP_CONFIG,
  OwnershipKey,
} from '@/_pages/interwill/components/Dashboard/TotalAssetsInfo';
import { EMPTY_VALUES as EMPTY_VALUES_DIRECT } from '@/_pages/interwill/components/Dashboard/TotalDirectAssets';
import { media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useTranslation } from '@/shared/utils/i18n/useTranslation';
import { numbers } from '@/shared/utils/numbers';

export const Dashboard = () => {
  const { t } = useTranslation('pages/pageInterwill', { keyPrefix: 'dashboard' });

  const totalAssetsChartDetails = (Object.keys(OWNERSHIP_CONFIG) as OwnershipKey[]).map(
    (ownershipType) => ({
      ownershipType,
      value: OWNERSHIP_CONFIG[ownershipType].emptyValue,
    }),
  );

  const totalAssetsChartData = {
    labels: totalAssetsChartDetails.map((item) =>
      t(OWNERSHIP_CONFIG[item.ownershipType].translationKey),
    ),
    datasets: totalAssetsChartDetails.map(({ value, ownershipType }) => ({
      label: t('totalInRepoChart.title'),
      data: [value],
      backgroundColor: OWNERSHIP_CONFIG[ownershipType].color,
      barPercentage: 2,
      categoryPercentage: 1.0,
    })),
  };

  const directAssetsChartData = [
    { label: t('totalDirectChart.added'), value: EMPTY_VALUES_DIRECT.added },
    {
      label: t('totalDirectChart.notAdded'),
      value: EMPTY_VALUES_DIRECT.notAdded,
    },
  ];

  return (
    <Container>
      <CategoryContainer>
        <Title>{t('overview')}</Title>
        <DashboardItems>
          <DashboardItem hasData={true}>
            <BarChartWrapper>
              <BarStackedChart data={totalAssetsChartData} />
            </BarChartWrapper>
            <Legend>
              <p>{t('totalInRepoChart.title')}</p>
              {totalAssetsChartDetails.map((item) => {
                return (
                  <TextContainer key={item.ownershipType}>
                    <Icon $bgColor={OWNERSHIP_CONFIG[item.ownershipType].color} />
                    <p>{t(OWNERSHIP_CONFIG[item.ownershipType].translationKey)}:</p>
                    <Value>{item.value}</Value>
                  </TextContainer>
                );
              })}
            </Legend>
          </DashboardItem>

          <DashboardItem hasData={true}>
            <DoughnutChartWrapper>
              <DoughnutChart
                title={t('totalDirectChart.title')}
                labels={directAssetsChartData.map((slice) => slice.label)}
                data={directAssetsChartData.map((slice) => slice.value)}
                colors={COLORS.colorDirectAssetsCharts}
                valueType="percent"
              />
            </DoughnutChartWrapper>
            <Legend>
              <p>{t('totalDirectChart.title')}</p>
              {directAssetsChartData.map(({ label, value }, index) => (
                <TextContainer key={label}>
                  <Icon $bgColor={COLORS.colorDirectAssetsCharts[index]} />
                  <p>{label}:</p>
                  <Value>
                    {numbers.formatPercent({ value, withSign: true, fractionDigits: 0 })}
                  </Value>
                </TextContainer>
              ))}
            </Legend>
          </DashboardItem>
        </DashboardItems>
      </CategoryContainer>
      <CategoryContainer>
        <Title>{t('distribution.title')}</Title>
        <DashboardItem hasData={true}>
          <DoughnutChartWrapper>
            <DoughnutChart
              title={t('distribution.byShares')}
              data={EMPTY_VALUES.data}
              labels={EMPTY_VALUES.labels}
              colors={COLORS.colorAssetDistributionCharts}
              valueType={'percent'}
            />
          </DoughnutChartWrapper>
          <Legend>
            <StyledSegmented
              value={'shares'}
              size="large"
              options={[
                { label: t('distribution.byShares'), value: 'shares' },
                { label: t('distribution.byValue'), value: 'value' },
              ]}
            />
            {EMPTY_VALUES.data.map((item, i) => (
              <TextContainer className={'textContainer'} key={item}>
                <Icon $bgColor={COLORS.colorAssetDistributionCharts[i]} />
                <Mask />
                <Value>
                  {numbers.formatPercent({
                    value: item,
                    withSign: true,
                    fractionDigits: 1,
                  })}
                </Value>
              </TextContainer>
            ))}
          </Legend>
        </DashboardItem>
      </CategoryContainer>
    </Container>
  );
};

const Container = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 16px;
  padding: 24px;
  width: 100%;
  pointer-events: none;

  ${media.iwMobile} {
    grid-template-columns: 1fr;
    padding: 12px;
  }
`;

const CategoryContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Title = styled.h3`
  font-weight: 700;
  font-size: 14px;
  line-height: 20px;

  ${media.iwMobile} {
    font-size: 12px;
  }
`;

const DashboardItems = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  gap: 16px;

  ${media.iwMobile} {
    flex-direction: column;
    align-items: start;
  }
`;

const BarChartWrapper = styled.div`
  width: 58px;
  min-height: 135px;
  height: 100%;
  position: relative;

  ${media.iwMobile} {
    margin-inline: 38px;
  }
`;

const Icon = styled.div<{ $bgColor: string }>`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ $bgColor }) => $bgColor};
`;

const Value = styled.p`
  color: ${COLORS.systemDark300};
`;

const Mask = styled.div`
  width: 85px;
  background: ${COLORS.systemLight300};
  height: 17px;
  border-radius: 3px;

  .textContainer:nth-child(2) & {
    width: 76px;
  }

  .textContainer:nth-child(3) & {
    width: 65px;
  }

  .textContainer:nth-child(4) & {
    width: 75px;
  }

  .textContainer:nth-child(5) & {
    width: 79px;
  }

  .textContainer:nth-child(6) & {
    width: 85px;
  }
`;

const StyledSegmented = styled(Segmented)`
  .ant-segmented-item-label {
    font-size: 14px !important;
    height: 30px !important;
    min-height: 30px !important;
    line-height: 30px !important;
  }
`;
